import React from 'react';

const SECTOR_COLORS = {
  'Technology':             { color:'#818cf8', bg:'rgba(129,140,248,0.1)', border:'rgba(129,140,248,0.25)' },
  'Financial Services':     { color:'#34d399', bg:'rgba(52,211,153,0.1)',  border:'rgba(52,211,153,0.25)' },
  'Healthcare':             { color:'#f472b6', bg:'rgba(244,114,182,0.1)', border:'rgba(244,114,182,0.25)' },
  'Consumer Cyclical':      { color:'#fb923c', bg:'rgba(251,146,60,0.1)',  border:'rgba(251,146,60,0.25)' },
  'Energy':                 { color:'#facc15', bg:'rgba(250,204,21,0.1)',  border:'rgba(250,204,21,0.25)' },
  'Industrials':            { color:'#94a3b8', bg:'rgba(148,163,184,0.1)', border:'rgba(148,163,184,0.25)' },
  'Communication Services': { color:'#22d3ee', bg:'rgba(34,211,238,0.1)',  border:'rgba(34,211,238,0.25)' },
  'Utilities':              { color:'#a78bfa', bg:'rgba(167,139,250,0.1)', border:'rgba(167,139,250,0.25)' },
  'Real Estate':            { color:'#f59e0b', bg:'rgba(245,158,11,0.1)',  border:'rgba(245,158,11,0.25)' },
  'Consumer Defensive':     { color:'#4ade80', bg:'rgba(74,222,128,0.1)',  border:'rgba(74,222,128,0.25)' },
  'Basic Materials':        { color:'#fb7185', bg:'rgba(251,113,133,0.1)', border:'rgba(251,113,133,0.25)' },
};
const DEFAULT_SECTOR = { color:'#64748b', bg:'rgba(100,116,139,0.1)', border:'rgba(100,116,139,0.2)' };

const PE_AVG = {
  'Technology':28,'Financial Services':15,'Healthcare':22,'Consumer Cyclical':20,
  'Energy':12,'Industrials':18,'Communication Services':24,'Utilities':16,
  'Real Estate':30,'Consumer Defensive':19,'Basic Materials':14,
};

// Market cap tier — institutional classification
function capTier(marketCap) {
  if (!marketCap) return null;
  const mc = parseFloat(marketCap);
  if (isNaN(mc) || mc === 0) return null;
  // marketCap in thousands (AV format) or raw dollars
  // AV returns raw dollars (e.g. 3700000000000 for $3.7T)
  if (mc >= 200e9)  return { label: 'Mega Cap',  color: '#818cf8', bg: 'rgba(129,140,248,0.1)', border: 'rgba(129,140,248,0.2)' };
  if (mc >= 10e9)   return { label: 'Large Cap', color: '#00d4ff', bg: 'rgba(0,212,255,0.08)', border: 'rgba(0,212,255,0.2)' };
  if (mc >= 2e9)    return { label: 'Mid Cap',   color: '#ffaa00', bg: 'rgba(255,170,0,0.08)', border: 'rgba(255,170,0,0.2)' };
  return              { label: 'Small Cap', color: '#ff8800', bg: 'rgba(255,136,0,0.08)', border: 'rgba(255,136,0,0.2)' };
}

export default function HeroCompany({ data, memberAnalysis }) {
  const px  = parseFloat(data.quote?.price || 0);
  const f   = data.fundamentals || {};
  const sc  = SECTOR_COLORS[data.sector] || DEFAULT_SECTOR;

  // Post-analysis enrichment
  const md          = memberAnalysis?.marketData || {};
  const description = md.description || null;
  const industry    = md.industry    || null;
  const capInfo     = capTier(md.marketCap || f.marketCap);

  // 52W range
  const lo    = parseFloat(f.week52Low  || 0);
  const hi    = parseFloat(f.week52High || 0);
  const has52W = hi > 0 && lo > 0;
  const pct52  = has52W && hi > lo ? Math.round(((px - lo) / (hi - lo)) * 100) : 0;
  const clamp  = Math.min(98, Math.max(2, pct52));
  const bc     = pct52 >= 80 ? '#ff4444' : pct52 >= 40 ? '#00ff88' : '#ffaa00';

  // PE valuation
  const pe    = parseFloat(f.peRatio || 0);
  const avg   = PE_AVG[data.sector] || 20;
  const hasPE = pe > 0;
  const ratio = hasPE ? pe / avg : 0;
  const grade = ratio > 1.5 ? 'OVERVALUED' : ratio < 0.75 ? 'UNDERVALUED' : 'FAIRLY VALUED';
  const gc    = grade === 'OVERVALUED' ? '#ff4444' : grade === 'UNDERVALUED' ? '#00ff88' : '#ffaa00';
  const gi    = grade === 'OVERVALUED' ? '⚠️' : grade === 'UNDERVALUED' ? '✅' : '〰️';

  return (
    <div style={{ flex:'1 1 0', padding:'1.4rem 1.5rem', display:'flex',
      flexDirection:'column', justifyContent:'flex-start', gap:'0.6rem', minWidth:0 }}>

      <div style={{ fontSize:'0.68rem', fontWeight:700, letterSpacing:'0.1em',
        color:'#475569', textTransform:'uppercase' }}>Company</div>

      {/* Sector pill + Cap tier side by side when available */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:'0.35rem', alignItems:'center' }}>
        {data.sector && (
          <div style={{ display:'inline-flex', alignItems:'center', gap:'0.35rem',
            padding:'0.25rem 0.7rem', background:sc.bg, border:'1px solid '+sc.border,
            borderRadius:'20px' }}>
            <span style={{ fontSize:'0.7rem', fontWeight:800, color:sc.color, letterSpacing:'0.04em' }}>
              📊 {data.sector}
            </span>
          </div>
        )}
        {capInfo && (
          <div style={{ display:'inline-flex', alignItems:'center',
            padding:'0.2rem 0.55rem', background:capInfo.bg, border:'1px solid '+capInfo.border,
            borderRadius:'20px' }}>
            <span style={{ fontSize:'0.65rem', fontWeight:800, color:capInfo.color, letterSpacing:'0.04em' }}>
              {capInfo.label}
            </span>
          </div>
        )}
      </div>

      {/* Industry — post-analysis, more specific than sector */}
      {industry && (
        <div style={{ fontSize:'0.68rem', color:'#64748b', fontWeight:600,
          paddingLeft:'0.1rem', letterSpacing:'0.01em' }}>
          {industry}
        </div>
      )}

      {/* Company description — post-analysis, 160 chars max */}
      {description && (
        <div style={{ fontSize:'0.72rem', color:'#94a3b8', lineHeight:1.55,
          padding:'0.4rem 0.55rem', background:'rgba(255,255,255,0.02)',
          border:'1px solid rgba(255,255,255,0.05)', borderRadius:'6px' }}>
          {description.length > 160 ? description.slice(0, 160) + '…' : description}
        </div>
      )}

      {/* Exchange + Country + IPO row */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:'0.25rem 0.6rem' }}>
        {data.exchange && (
          <span style={{ fontSize:'0.69rem', color:'#64748b', fontWeight:600 }}>🏛 {data.exchange}</span>
        )}
        {data.country && (
          <span style={{ fontSize:'0.69rem', color:'#64748b', fontWeight:600 }}>🌍 {data.country}</span>
        )}
        {data.ipo && (
          <span style={{ fontSize:'0.69rem', color:'#64748b', fontWeight:600 }}>📅 IPO {data.ipo}</span>
        )}
      </div>

      {/* Website + Employees row */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:'0.25rem 0.6rem' }}>
        {data.website && (
          <a href={data.website} target="_blank" rel="noopener noreferrer"
            style={{ fontSize:'0.69rem', color:'#00d4ff', textDecoration:'none', fontWeight:600 }}>
            🌐 {data.website.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]}
          </a>
        )}
        {data.employees && (
          <span style={{ fontSize:'0.69rem', color:'#64748b', fontWeight:600 }}>
            👥 {Number(data.employees).toLocaleString()}
          </span>
        )}
      </div>

      <div style={{ height:'1px', background:'rgba(255,255,255,0.05)', margin:'0.1rem 0' }} />

      {/* 52W range bar */}
      {has52W && (
        <div>
          <div style={{ display:'flex', justifyContent:'space-between',
            fontSize:'0.62rem', color:'#475569', marginBottom:'0.28rem', fontWeight:600 }}>
            <span>52W Low ${lo.toFixed(2)}</span>
            <span style={{ color:bc, fontWeight:700 }}>{pct52}% of high</span>
            <span>High ${hi.toFixed(2)}</span>
          </div>
          <div style={{ position:'relative', height:'5px', background:'rgba(255,255,255,0.08)', borderRadius:'4px' }}>
            <div style={{ position:'absolute', left:0, top:0, height:'100%',
              width:clamp+'%', background:bc, borderRadius:'4px' }} />
            <div style={{ position:'absolute', top:'-3px', left:clamp+'%',
              transform:'translateX(-50%)', width:'11px', height:'11px', borderRadius:'50%',
              background:bc, border:'2px solid #0a0f1e' }} />
          </div>
        </div>
      )}

      {/* PE valuation badge */}
      {hasPE && (
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
          padding:'0.4rem 0.7rem', background:gc+'10', borderRadius:'8px', border:'1px solid '+gc+'33' }}>
          <span style={{ fontSize:'0.7rem', color:'#64748b', fontWeight:600 }}>
            P/E {pe.toFixed(1)}x · avg {avg}x
          </span>
          <span style={{ fontSize:'0.74rem', fontWeight:800, color:gc }}>{gi} {grade}</span>
        </div>
      )}
    </div>
  );
}
